import Box from '@mui/material/Box';
import { useContext } from 'react';

import ApprovalActionsColumn from '~components/Load/LoadsTableColumns/ApprovalActionsColumn';
import DatetimeColumn from '~components/Load/LoadsTableColumns/DatetimeColumn';
import DriverAndTruckColumn from '~components/Load/LoadsTableColumns/DriverAndTruckColumn';
import PreviewColumn from '~components/Load/LoadsTableColumns/PreviewColumn';
import QuantityAndMaterialColumn from '~components/Load/LoadsTableColumns/QuantityAndMaterialColumn';
import WaypointsColumn from '~components/Load/LoadsTableColumns/WaypointsColumn';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import { flaggedLoadRowSx } from '~utils/loads/load-utils';

import SettlementLoadsQueryContext from '../SettlementLoadsQueryContext';
import { columns, gridTemplateColumns } from './settlement-loads-table-columns';

interface SettlementLoadsTableRowProps {
  job?: Job;
  load: Load;
  loadNumber: number;
  onClick: (loadNumber: number, load: Load, ticket?: Ticket) => void;
  ticket?: Ticket;
}

export default function SettlementLoadsTableRow({
  job,
  load,
  loadNumber,
  onClick,
  ticket,
}: SettlementLoadsTableRowProps) {
  const { idsOfBusyLoads, refreshLoads, settlement, updateStoredLoad } = useContext(
    SettlementLoadsQueryContext,
  );

  const isBusyRow = idsOfBusyLoads.includes(load.id);
  const isSettlementEditable = settlement.editable;

  const handleRowClick = () => {
    if (!isBusyRow) {
      onClick(loadNumber, load, ticket);
    }
  };

  return (
    <Box
      onClick={isBusyRow ? undefined : handleRowClick}
      sx={(theme) => ({
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        cursor: 'pointer',
        display: 'grid',
        gridAutoRows: 'auto',
        gridColumn: `span ${columns.length}`,
        gridTemplateColumns,
        overflow: 'hidden',
        ...(ticket?.isPotentialDuplicateFlagged && flaggedLoadRowSx(theme)),
        ...(isBusyRow
          ? {
              bgcolor: theme.brandV2.colors.treadGray10,
              borderBottomColor: theme.brandV2.colors.treadGray6,
              cursor: 'not-allowed',
              opacity: 0.5,
              pointerEvents: 'none',
            }
          : {}),
      })}
    >
      <DatetimeColumn ticket={ticket} sx={{ ...columns[0].sx }} />
      <PreviewColumn ticket={ticket} sx={{ ...columns[1].sx }} />
      <DriverAndTruckColumn job={job} ticket={ticket} sx={{ ...columns[2].sx }} />
      <QuantityAndMaterialColumn
        job={job}
        load={load}
        ticket={ticket}
        sx={{ ...columns[3].sx }}
      />
      <WaypointsColumn load={load} sx={{ ...columns[4].sx }} />
      <ApprovalActionsColumn
        isOrderEditable={isSettlementEditable}
        jobId={ticket?.friendlyJobId ?? undefined}
        load={load}
        onLoadUpdate={updateStoredLoad}
        onAiReviewDismissed={() => void refreshLoads()}
        ticket={ticket}
        sx={{ ...columns[5].sx }}
      />
    </Box>
  );
}
